/**
 * Разбор проблем хозяйства по ТИПУ — чистая логика без разметки и запросов
 * (agroai-6al.5, план `docs/research/2026-10-03-page-plan-v2.md` §1, §12).
 *
 * Тип проблемы — то, что видит человек: «Сроки», «Вода», «Расхождения»,
 * «Карантин» и «Совпадение» (сроки + вода на одних контурах). Сервер
 * называет ВИД сигнала (`SituationKind`) и ЛИНЗУ очереди; здесь их перевод
 * на тип, вкладки, заголовок-действие и выборки для шаблонов разбора.
 *
 * Правила учётной рамки (§0.4): «без отчёта» ≠ «не сделано»; отсутствие ≠
 * ноль (`null` печатается словами); оси «проверено» нет — у «Расхождений»
 * говорим об отметке проверяющего, а не о доле проверенного.
 */
import type { Schemas } from '../../../api/client';
import type { Situation, SituationLens } from '../situations';
import { countOf, num, WORDS } from '../../lib/format';
import { defineMessages, type PlainMessage } from '../../../i18n/define';
import { tr } from '../../../i18n/runtime';

/* Тексты разбора проблем (пространство board). Все — сообщения каталога; строки
   собираются при вызове (`tr`), не при загрузке модуля. */
const msg = defineMessages({
  labelDeadlines: { id: 'board.problems.kind.deadlines', defaultMessage: 'Сроки', description: 'Тип проблемы хозяйства (вкладка очереди, метка карточки, менеджер): работы после срока без отчёта' },
  labelWater: { id: 'board.problems.kind.water', defaultMessage: 'Вода', description: 'Тип проблемы хозяйства (вкладка очереди, метка карточки): недоподача воды на поливе' },
  labelConflicts: { id: 'board.problems.kind.conflicts', defaultMessage: 'Расхождения', description: 'Тип проблемы хозяйства (вкладка очереди, метка карточки): отчёт расходится с отметкой проверяющего' },
  labelQuarantine: { id: 'board.problems.kind.quarantine', defaultMessage: 'Карантин', description: 'Тип проблемы хозяйства (вкладка очереди, метка карточки): карантинные контуры и предписания' },
  labelOverlap: { id: 'board.problems.kind.overlap', defaultMessage: 'Сроки и вода', description: 'Тип проблемы хозяйства: срок прошёл и по тем же контурам есть водная строка (метка карточки)' },
  tabAll: { id: 'board.problems.tab.all', defaultMessage: 'Все', description: 'Вкладка очереди проблем: все типы сразу; короткая' },
  tabCountNote: {
    id: 'board.problems.tabCountNote',
    defaultMessage: 'Числа на вкладках — хозяйства. «Все» — хозяйства хотя бы с одним сигналом любой категории; хозяйство с несколькими сигналами — одна строка с несколькими значками, поэтому сумма категорий больше «Всех». Порядок — по критичности: сначала хозяйства выше порога существенности, затем с большим числом категорий сразу.',
    description: 'Пояснение в «i»-подсказке карточки «Где нужно разобраться» (менеджер): что считают числа на вкладках и как упорядочена очередь',
  },
  actionDeadlinesSoon: { id: 'board.problems.action.deadlinesSoon', defaultMessage: 'Сроки: окно закрывается — уточнить дату отчёта', description: 'Заголовок-действие карточки проблемы: просрочки нет, но окно скоро закроется' },
  actionDeadlines: { id: 'board.problems.action.deadlines', defaultMessage: 'Сроки: получить отчёты по закрытым окнам', description: 'Заголовок-действие карточки проблемы: окна закрыты, отчётов нет' },
  actionWater: { id: 'board.problems.action.water', defaultMessage: 'Вода: поливы без подачи по файлу ирригации', description: 'Заголовок-действие карточки проблемы по воде: поливы с закрытым окном без события в файле ирригации' },
  actionConflicts: { id: 'board.problems.action.conflicts', defaultMessage: 'Расхождение: сверить отчёт и отметку проверяющего', description: 'Заголовок-действие карточки проблемы: расхождение отчёта и проверки' },
  actionQuarantine: { id: 'board.problems.action.quarantine', defaultMessage: 'Карантин: проверить предписания по контурам', description: 'Заголовок-действие карточки проблемы: карантин' },
  actionOverlap: { id: 'board.problems.action.overlap', defaultMessage: 'Вода: поливы без подачи по файлу ирригации', description: 'Заголовок-действие карточки проблемы: срок полива прошёл, события в файле ирригации нет' },
  checkDeadlines: { id: 'board.problems.check.deadlines', defaultMessage: 'Связаться с хозяйством, уточнить выполнение и получить отчёт с датой и автором. Нет отчёта — повод уточнить, а не вывод «не сделано».', description: 'Текст после «Что проверить.» в карточке проблемы «Сроки» (менеджер): действие, не оценка' },
  checkWater: { id: 'board.problems.check.water', defaultMessage: 'Уточнить у водной службы, был ли полив на контурах без события в файле ирригации, и при необходимости получить исправленный файл. Нет события — повод уточнить, а не вывод «не полито».', description: 'Текст после «Что проверить.» в карточке проблемы «Вода» (вода — события файла ирригации; заявок в пилоте нет)' },
  checkConflicts: { id: 'board.problems.check.conflicts', defaultMessage: 'Сопоставить первичный отчёт и отметку проверяющего. Зафиксировать уточнение; отчёт остаётся фактом, пока не уточнён.', description: 'Текст после «Что проверить.» в карточке проблемы «Расхождения»' },
  checkQuarantine: { id: 'board.problems.check.quarantine', defaultMessage: 'Проверить исполнение предписаний на красных и жёлтых контурах и срок повторного обследования.', description: 'Текст после «Что проверить.» в карточке проблемы «Карантин»' },
  checkOverlap: { id: 'board.problems.check.overlap', defaultMessage: 'Уточнить у водной службы, был ли полив на контурах без события в файле ирригации, и при необходимости получить исправленный файл. Нет события — повод уточнить, а не вывод «не полито».', description: 'Текст после «Что проверить.» в карточке проблемы «Сроки и вода»' },
  explainOverdue: {
    id: 'board.problems.explain.overdue',
    defaultMessage: '{value} {count, plural, one {работа на поле} few {работы на полях} many {работ на полях} other {работы на полях}} без отчёта после окончания окна.',
    description: 'Объяснение строки очереди во вкладке «Сроки» (менеджер); {value} — число работ (или «—», если не измерено), {count} — то же число для склонения слова',
  },
  explainOverdueSoon: {
    id: 'board.problems.explain.overdueSoon',
    defaultMessage: '{value} {count, plural, one {работа на поле} few {работы на полях} many {работ на полях} other {работы на полях}} без отчёта после окончания окна, ещё {soon} — в окнах, которые скоро закроются.',
    description: 'Объяснение строки во вкладке «Сроки», когда есть и просрочка, и скорые окна; {value}/{count} — просроченные работы, {soon} — число работ в скоро закрывающихся окнах',
  },
  explainSoon: {
    id: 'board.problems.explain.soon',
    defaultMessage: '{value} {count, plural, one {работа на поле} few {работы на полях} many {работ на полях} other {работы на полях}} без отчёта в окнах, которые скоро закроются. Окно ещё идёт.',
    description: 'Объяснение строки во вкладке «Сроки», когда просрочки нет; {value}/{count} — число работ без отчёта в скоро закрывающихся окнах (или «—»)',
  },
  explainWater: {
    id: 'board.problems.explain.water',
    defaultMessage: '{value} {count, plural, one {полив} few {полива} many {поливов} other {полива}} без подачи: окно полива на контуре закрылось, а события в файле ирригации нет.',
    description: 'Объяснение во вкладке «Вода»; {value}/{count} — число поливов без подачи (контур × полив с закрытым окном без события файла ирригации) или «—»',
  },
  explainConflict: {
    id: 'board.problems.explain.conflict',
    defaultMessage: '{value} {count, plural, one {отчёт опровергнут} few {отчёта опровергнуты} many {отчётов опровергнуто} other {отчёта опровергнуты}} проверяющим. Нужно уточнить основание расхождения.',
    description: 'Объяснение строки во вкладке «Расхождения», когда есть опровергнутые отчёты; {value}/{count} — их число',
  },
  explainAwaiting: {
    id: 'board.problems.explain.awaiting',
    defaultMessage: '{value} {count, plural, one {отчёт} few {отчёта} many {отчётов} other {отчёта}} без отметки проверяющего; они приняты как факт.',
    description: 'Объяснение строки во вкладке «Расхождения», когда опровергнутых нет, но есть отчёты без отметки проверяющего; {value}/{count} — их число (или «—»)',
  },
  explainQuarantine: {
    id: 'board.problems.explain.quarantine',
    defaultMessage: '{flagged} из {value} {count, plural, one {контура} few {контуров} many {контуров} other {контуров}} хозяйства с карантинным сигналом.',
    description: 'Объяснение строки во вкладке «Карантин»; {flagged} — контуров с сигналом, {value}/{count} — все контуры хозяйства (или «—»)',
  },
  explainOverlap: {
    id: 'board.problems.explain.overlap',
    defaultMessage: '{value} {count, plural, one {полив} few {полива} many {поливов} other {полива}} без подачи: окно полива на контуре закрылось, а события в файле ирригации нет.',
    description: 'Объяснение строки, где срок полива прошёл без события файла ирригации; {value}/{count} — число поливов (контур × полив) или «—»',
  },
  dirWaterCount: { id: 'board.problems.dir.waterCount', defaultMessage: '{count} без подачи', description: 'Число на вкладке направления «Вода» в разборе хозяйства: поливов без подачи; коротко' },
  dirConflictsCount: { id: 'board.problems.dir.conflictsCount', defaultMessage: '{count} спорн.', description: 'Число на вкладке направления «Расхождения»: отчётов, опровергнутых проверяющим; очень коротко' },
  dirQuarantineCount: { id: 'board.problems.dir.quarantineCount', defaultMessage: '{contours}', description: 'Число на вкладке направления «Карантин»: контуров с сигналом словом («3 контура»)' },
  dirDeadlinesHa: { id: 'board.problems.dir.deadlinesHa', defaultMessage: '{ha} га', description: 'Число на вкладке направления «Сроки»: гектары земли с работой после срока; коротко' },
  dirDeadlinesWorks: { id: 'board.problems.dir.deadlinesWorks', defaultMessage: '{count} без отчёта', description: 'Число на вкладке направления «Сроки», когда площадь не названа: работ без отчёта; коротко' },
  waterNoDelivery: { id: 'board.problems.water.noDelivery', defaultMessage: 'без подачи — окно закрылось, события нет', description: 'Подпись цвета в легенде карты разбора «Вода»: окно полива закрылось, события в файле ирригации нет' },
  waterLayerTitle: { id: 'board.problems.water.layerTitle', defaultMessage: 'Полив по файлу ирригации', description: 'Заголовок легенды карты разбора «Вода»' },
  waterLayerNone: { id: 'board.problems.water.layerNone', defaultMessage: 'полито по файлу или полива нет в плане', description: 'Подпись фона в легенде карты разбора «Вода»: контур без проблемы по выбранному поливу (полито событием файла или полива нет в плане)' },
  deadlineLayerTitle: { id: 'board.problems.deadline.layerTitle', defaultMessage: 'Где нет отчёта', description: 'Заголовок легенды карты разбора «Сроки»: подсвечены только контуры с проблемой' },
  deadlineOverdue: { id: 'board.problems.deadline.overdue', defaultMessage: 'после срока — отчёта нет', description: 'Подпись цвета в легенде карты «Сроки»: окно закрылось, отчёта нет' },
  deadlineClosing: { id: 'board.problems.deadline.closing', defaultMessage: 'окно закрывается — отчёта пока нет', description: 'Подпись цвета в легенде карты «Сроки»: окно скоро закроется, отчёта нет' },
  problemLayerNone: { id: 'board.problems.layer.problemNone', defaultMessage: 'остальная земля хозяйства — без проблемы', description: 'Подпись фона в легенде карты разбора: контуры хозяйства без проблемы по выбранному пункту' },
  roleVerifier: { id: 'board.problems.role.verifier', defaultMessage: 'проверяющий', description: 'Роль того, кто поставил отметку проверки; строчными, встаёт в строку «проверил: …»' },
  roleManager: { id: 'board.problems.role.manager', defaultMessage: 'хокимият', description: 'Роль того, кто поставил отметку проверки: менеджер хокимията; строчными' },
  roleFarm: { id: 'board.problems.role.farm', defaultMessage: 'хозяйство', description: 'Роль того, кто поставил отметку проверки: само хозяйство; строчными' },
  outcomeConflict: { id: 'board.problems.outcome.conflict', defaultMessage: 'отчёт опровергнут', description: 'Исход проверки отчёта (бейдж в строке расхождения): отчёт опровергнут; строчными, коротко' },
  outcomeClarify: { id: 'board.problems.outcome.clarify', defaultMessage: 'нужно уточнение', description: 'Исход проверки отчёта (бейдж в строке расхождения): проверяющий просит уточнить; строчными, коротко' },
  layerTitle: { id: 'board.problems.layer.title', defaultMessage: 'Расхождения', description: 'Название слоя карты разбора: спорные контуры' },
  layerLegend: { id: 'board.problems.layer.legend', defaultMessage: 'Расхождение отчёта и проверки', description: 'Подпись цвета в легенде слоя карты: контур с расхождением отчёта и проверки' },
  layerNone: { id: 'board.problems.layer.none', defaultMessage: 'Расхождения нет', description: 'Подпись цвета в легенде слоя карты: остальная земля хозяйства без расхождения' },
});

/* ------------------------------------------------------------ типы */

/** Тип проблемы — шаблон разбора. */
export type ProblemKind = 'deadlines' | 'water' | 'conflicts' | 'quarantine' | 'overlap';
/** Вкладка очереди: тип проблемы или «Все». Совпадение живёт во «Всех». */
export type ProblemTab = 'all' | Exclude<ProblemKind, 'overlap'>;

export const PROBLEM_LABEL: Record<ProblemKind, PlainMessage> = {
  deadlines: msg.labelDeadlines,
  water: msg.labelWater,
  conflicts: msg.labelConflicts,
  quarantine: msg.labelQuarantine,
  overlap: msg.labelOverlap,
};

/** Вкладки очереди; подпись — сообщение каталога (`t(tab.name)`). */
export const TABS: { id: ProblemTab; name: PlainMessage }[] = [
  { id: 'all', name: msg.tabAll },
  { id: 'deadlines', name: msg.labelDeadlines },
  { id: 'water', name: msg.labelWater },
  { id: 'conflicts', name: msg.labelConflicts },
  { id: 'quarantine', name: msg.labelQuarantine },
];

/**
 * Вкладка → линза сервера (agroai-6al.35, журнал №291).
 *
 * «Все» — линза `any`: ОБЪЕДИНЕНИЕ категорий (Сроки ∪ Вода ∪ Расхождения ∪
 * Карантин) без порога. Раньше «Все» читала линзу `all` — только хозяйства,
 * прошедшие порог существенности, — а вкладки категорий порога не имели:
 * «Все 6» при «Расхождения 73». Порог существенности теперь решает только
 * порядок (существенные сверху), а не членство.
 *
 * «Сроки» — линза `deadlines`: после срока без отчёта ИЛИ окно скоро
 * закроется. Прежняя `window` давала только второе и теряла хозяйства с
 * просрочкой, хотя KPI «После срока» вёл именно сюда.
 */
export const TAB_LENS: Record<ProblemTab, SituationLens> = {
  all: 'any',
  deadlines: 'deadlines',
  water: 'water',
  conflicts: 'verification',
  quarantine: 'quarantine',
};

/** Подпись под вкладками: что считают счётчики (agroai-6al.35). */
export const TAB_COUNT_NOTE: PlainMessage = msg.tabCountNote;

/** Вид сигнала сервера → тип проблемы. Неизвестный вид — «Сроки» (самый общий). */
export function kindOfSignal(kind: string | null | undefined): ProblemKind {
  switch (kind) {
    case 'report_conflict':
    case 'verification_pending':
      return 'conflicts';
    case 'water_gap_after_window':
      return 'water';
    case 'combined_overdue_and_water':
      return 'overlap';
    case 'quarantine':
      return 'quarantine';
    default:
      return 'deadlines';
  }
}

/** Какой шаблон разбора открыть: вкладка типа решает сама, «Все» — по виду строки. */
export function reviewKindFor(tab: ProblemTab, situation: Pick<Situation, 'kind'> | null | undefined): ProblemKind {
  if (tab !== 'all') return tab;
  return kindOfSignal(situation?.kind);
}

/**
 * Вкладки, которые показываем. Карантин — только если сервер назвал счётчик
 * его линзы (6al.2); без него вкладка скрыта, а не «0» (отсутствие ≠ ноль).
 */
export function visibleTabs(byLens: Partial<Record<SituationLens, number>> | null | undefined): ProblemTab[] {
  return TABS.map(t => t.id).filter(id => id !== 'quarantine' || typeof byLens?.quarantine === 'number');
}

/** Счётчик хозяйств вкладки; `null` — сервер не назвал. */
export function tabCount(tab: ProblemTab, byLens: Partial<Record<SituationLens, number>> | null | undefined): number | null {
  const v = byLens?.[TAB_LENS[tab]];
  return typeof v === 'number' ? v : null;
}

/** Заголовок-действие по типу (как в K2: «Полив: сверить отчёты и подачу»). */
export function actionTitle(kind: ProblemKind, s?: Pick<Situation, 'overdue' | 'dueSoon'> | null): string {
  switch (kind) {
    case 'deadlines':
      return tr(s && s.overdue === 0 && s.dueSoon > 0 ? msg.actionDeadlinesSoon : msg.actionDeadlines);
    case 'water':
      return tr(msg.actionWater);
    case 'conflicts':
      return tr(msg.actionConflicts);
    case 'quarantine':
      return tr(msg.actionQuarantine);
    case 'overlap':
      return tr(msg.actionOverlap);
  }
}

/** «Что проверить» — текст из правил ситуаций, по типу. Действие, не оценка. */
export const WHAT_TO_CHECK: Record<ProblemKind, PlainMessage> = {
  deadlines: msg.checkDeadlines,
  water: msg.checkWater,
  conflicts: msg.checkConflicts,
  quarantine: msg.checkQuarantine,
  overlap: msg.checkOverlap,
};

/**
 * Объяснение строки под ВЫБРАННЫЙ тип. Сервер пишет объяснение по своему
 * главному виду сигнала; во вкладке другого типа оно говорило бы о другом
 * («3 отчёта опровергнуто» во вкладке «Вода»). Совпал тип — текст сервера,
 * иначе — фраза из чисел той же строки.
 */
export function explainFor(kind: ProblemKind, s: Situation): string {
  // Вода — всегда фраза из чисел строки: она говорит о событиях файла ирригации (agroai-0e8).
  if (kindOfSignal(s.kind) === kind && kind !== 'water' && kind !== 'overlap') return s.explanation;
  /** Число для склонения слова (6al.42): не измерено — «—» и форма множества. */
  const cnt = (v: number | null | undefined) => ({ value: v === null || v === undefined ? '—' : num(v), count: v ?? 0 });
  switch (kind) {
    case 'deadlines':
      return s.overdue > 0
        ? (s.dueSoon ? tr(msg.explainOverdueSoon, { ...cnt(s.overdue), soon: num(s.dueSoon) }) : tr(msg.explainOverdue, cnt(s.overdue)))
        : tr(msg.explainSoon, cnt(s.dueSoon));
    case 'water':
      return tr(msg.explainWater, cnt(s.waterGapObligations ?? (s.overlapObligations > 0 ? s.overlapObligations : null)));
    case 'conflicts':
      return s.conflicts > 0 ? tr(msg.explainConflict, cnt(s.conflicts)) : tr(msg.explainAwaiting, cnt(s.awaiting));
    case 'quarantine':
      return tr(msg.explainQuarantine, { ...cnt(s.contoursTotal), flagged: s.quarantine === null || s.quarantine === undefined ? '—' : num(s.quarantine) });
    case 'overlap':
      return tr(msg.explainOverlap, cnt(s.waterGapObligations ?? (s.overlapObligations > 0 ? s.overlapObligations : null)));
  }
}

/* ------------------------------------------------------------ значки сигналов */

export interface SignalCounts {
  /** Обязательств после срока без отчёта (или в закрывающихся окнах). */
  deadlines: number | null;
  /** Недоподача, м³ (если известна) — иначе обязательств с незакрытой подачей. */
  waterM3: number | null;
  waterObligations: number | null;
  conflicts: number | null;
  quarantine: number | null;
  /** Земля (каждый гектар один раз) с работой после срока — главное число значка сроков; `null` — не названа. */
  deadlinesHa?: number | null;
}

/** Числа для `SignalChips` из строки очереди (+ недоподача хозяйства, если есть). */
export function signalCounts(s: Situation, waterGapM3?: number | null): SignalCounts {
  return {
    deadlines: s.overdue > 0 ? s.overdue : s.dueSoon > 0 ? s.dueSoon : 0,
    waterM3: waterGapM3 ?? null,
    waterObligations: s.waterGapObligations ?? (s.overlapObligations > 0 ? s.overlapObligations : 0),
    conflicts: s.conflicts,
    quarantine: s.quarantine ?? null,
    deadlinesHa: s.overdue > 0 && typeof s.overdueArea === 'number' ? s.overdueArea : null,
  };
}

/* ------------------------------------------------------------ очередь */

export interface DispositionTally {
  fresh: number;
  inWork: number;
  closed: number;
}

/**
 * «Сегодня: новых N · в работе M · закрыто K» — по загруженным строкам.
 *
 * Запасной путь: очередь приезжает страницей, и счёт по ней называет потолок
 * страницы размером очереди. Основной источник — `summary` ответа сервера
 * (`SituationsView.queueTally`, по всей очереди линзы, agroai-b48.3); сюда
 * доходит только ответ старого сервера, который итогов не присылает.
 */
export function dispositionTally(rows: readonly Pick<Situation, 'disposition'>[]): DispositionTally {
  const t = { fresh: 0, inWork: 0, closed: 0 };
  for (const r of rows) {
    const st = r.disposition?.status;
    if (!st) t.fresh += 1;
    else if (st === 'closed') t.closed += 1;
    else t.inWork += 1;
  }
  return t;
}

/** Закрытые строки по умолчанию скрыты. */
export function visibleRows<T extends Pick<Situation, 'disposition'>>(rows: readonly T[], showClosed: boolean): T[] {
  return showClosed ? [...rows] : rows.filter(r => r.disposition?.status !== 'closed');
}

/* ------------------------------------------------------------ даты и числа */

const DAY = 86_400_000;
const at = (iso: string) => Date.parse(`${iso.slice(0, 10)}T00:00:00Z`);
export const daysFrom = (from: string, to: string) => Math.round((at(to) - at(from)) / DAY);
const val = (m: { value: number | null } | null | undefined) => (m && m.value !== null && Number.isFinite(m.value) ? m.value : null);

export function median(values: readonly number[]): number | null {
  if (!values.length) return null;
  const s = [...values].sort((a, b) => a - b);
  const mid = Math.floor(s.length / 2);
  return s.length % 2 ? s[mid]! : (s[mid - 1]! + s[mid]!) / 2;
}

/** «Хлопок-2026: Полив №3» → «Полив №3»: цикл уже назван срезом. */
export const workName = (name: string) => name.replace(/^[^:]*:\s*/, '');

/* ------------------------------------------------------------ Сроки */

type CampaignWork = Schemas['SeasonCampaignWorkOut'];
type BreakdownRow = Schemas['SeasonBreakdownRowOut'];

export interface OverdueWork {
  campaignId: number;
  workDefId: number;
  name: string;
  category: string;
  obligations: number;
  overdue: number;
  noReport: number;
  areaHa: number | null;
  windowFrom: string | null;
  windowTo: string | null;
  /** Окно закрылось столько дней назад (≥0) или закроется через (<0). */
  closedDaysAgo: number | null;
  /** Контуры без отчёта по работе (сервер отдаёт до `limit`). */
  silentContours: string[];
  /** Ключ агроцикла работы (картина по нескольким циклам, agroai-0e8.23). */
  cycle?: string | null;
}

export function toOverdueWork(w: CampaignWork, asOf: string): OverdueWork {
  const area = w.area?.total_microha;
  return {
    campaignId: w.campaign_id,
    workDefId: w.work_def_id,
    name: workName(w.name),
    category: w.category,
    obligations: w.totals.obligations.total,
    overdue: val(w.totals.overdue) ?? 0,
    noReport: val(w.totals.no_report) ?? 0,
    areaHa: area === null || area === undefined ? null : area / 1e6,
    windowFrom: w.window_from ?? null,
    windowTo: w.window_to ?? null,
    closedDaysAgo: w.window_to ? daysFrom(w.window_to, asOf) : null,
    silentContours: (w.remaining_contours?.ids ?? []).map(String),
    cycle: w.cycle ?? null,
  };
}

/** Три работы с наибольшей просрочкой (после срока без отчёта). */
export function topOverdue(works: readonly CampaignWork[], asOf: string, n = 3): OverdueWork[] {
  return works
    .map(w => toOverdueWork(w, asOf))
    .filter(w => w.overdue > 0)
    // При равной просрочке выше — недавно закрытое окно: по нему отчёт ещё реально получить.
    .sort((a, b) => b.overdue - a.overdue || (b.windowTo ?? '').localeCompare(a.windowTo ?? ''))
    .slice(0, n);
}

/** Окна, которые закрываются в ближайшие `days` дней и где ещё нет отчётов. */
export function closingSoon(works: readonly CampaignWork[], asOf: string, days = 5): OverdueWork[] {
  return works
    .map(w => toOverdueWork(w, asOf))
    .filter(w => w.windowTo !== null && w.windowFrom !== null && w.windowFrom <= asOf && w.windowTo >= asOf && daysFrom(asOf, w.windowTo) <= days && w.noReport > 0)
    .sort((a, b) => (a.windowTo ?? '').localeCompare(b.windowTo ?? ''));
}

/** Доля «после срока» хозяйства против медианы хозяйств района. */
export function shareVsMedian(
  farm: { overdue: number | null; total: number },
  district: readonly BreakdownRow[],
  minObligations = 30,
): { share: number | null; median: number | null; peers: number } {
  const shares = district
    .filter(r => r.totals.obligations.total >= minObligations && val(r.totals.overdue) !== null)
    .map(r => (100 * val(r.totals.overdue)!) / r.totals.obligations.total);
  return {
    share: farm.overdue === null || !(farm.total > 0) ? null : (100 * farm.overdue) / farm.total,
    median: median(shares),
    peers: shares.length,
  };
}

/* ------------------------------------------------------------ Вода */

export interface IrrigationRow {
  campaignId: number;
  workDefId: number;
  name: string;
  windowFrom: string | null;
  windowTo: string | null;
  requested: number | null;
  delivered: number | null;
  /** Недоподача, м³: запрошено − подано, не меньше нуля. */
  gap: number | null;
  /** Контуров без отчёта о поливе. */
  silentContours: string[];
  /** Окно ещё не открылось — подачи и отчёта ждать рано. */
  ahead: boolean;
  /** Заявок по поливу в журнале нет (≠ «объём не измерен»). */
  noRequests: boolean;
  /** Контуры, где подача по поливу не подтверждена (разбор сервера). */
  gapContours?: string[];
  gapContoursTotal?: number;
  /** Номера контуров без подачи (для строки разбора); первые — от сервера. */
  gapContourNos?: string[];
  /** Вода по событиям файла ирригации (agroai-0e8): заявок нет, «запрошено» не считается. */
  fromEvents?: boolean;
  /** Обязательств полива (контур × полив) у хозяйства. */
  obligations?: number;
  /** Поливов (контур × полив), закрытых событием файла. */
  closed?: number;
  /** Контуров, где полив закрыт событием файла. */
  wateredContours?: number;
  /** Событий файла по поливу. */
  events?: number;
  /** Полито, га; `null` — не названо. */
  wateredHa?: number | null;
  /** Ключ агроцикла полива (картина по нескольким циклам). */
  cycle?: string | null;
}

/** Поливы хозяйства: работы категории irrigation + водная сводка по каждой. */
export function irrigationRows(
  works: readonly CampaignWork[],
  water: ReadonlyMap<number, { requested: number | null; delivered: number | null; rows?: number }>,
  asOf: string,
): IrrigationRow[] {
  return works
    .filter(w => w.category === 'irrigation')
    .map(w => {
      const o = toOverdueWork(w, asOf);
      const v = water.get(w.work_def_id);
      const requested = v?.requested ?? null;
      const delivered = v?.delivered ?? null;
      return {
        campaignId: w.campaign_id,
        cycle: o.cycle ?? null,
        workDefId: w.work_def_id,
        name: o.name,
        windowFrom: o.windowFrom,
        windowTo: o.windowTo,
        requested,
        delivered,
        gap: requested === null || delivered === null ? null : Math.max(0, requested - delivered),
        silentContours: o.windowFrom !== null && o.windowFrom <= asOf ? o.silentContours : [],
        ahead: o.windowFrom !== null && o.windowFrom > asOf,
        noRequests: v?.rows === 0,
      };
    })
    .sort((a, b) => (a.windowFrom ?? '').localeCompare(b.windowFrom ?? ''));
}

/** Полив по умолчанию: с наибольшим разрывом, иначе с отчётами «нет», иначе последний начавшийся. */
export function pickIrrigation(rows: readonly IrrigationRow[]): IrrigationRow | null {
  const started = rows.filter(r => !r.ahead);
  // По событиям файла: полив с наибольшим числом контуров без подачи.
  const byDry = [...started].filter(r => (r.gapContoursTotal ?? 0) > 0).sort((a, b) => (b.gapContoursTotal ?? 0) - (a.gapContoursTotal ?? 0));
  const byGap = [...started].filter(r => (r.gap ?? 0) > 0).sort((a, b) => (b.gap ?? 0) - (a.gap ?? 0));
  return byDry[0] ?? byGap[0] ?? started.find(r => r.silentContours.length > 0) ?? started.at(-1) ?? rows[0] ?? null;
}

export type WaterState = 'delivered' | 'partial' | 'none' | 'unmeasured';

/**
 * Состояние подачи по контурам одного полива одного хозяйства — для слоя
 * `water` карты. Частично — меньше 95 % запрошенного (как легенда слоя).
 */
export function waterByContour(
  allocations: ReadonlyArray<Pick<Schemas['WaterAllocationOut'], 'contour_id' | 'requested_volume_m3' | 'volume_m3'> & { actor?: { id: number } | null }>,
  actorId: number,
): Record<string, WaterState> {
  const sums = new Map<string, { req: number | null; del: number | null }>();
  for (const a of allocations) {
    if (a.actor?.id !== actorId || a.contour_id === null || a.contour_id === undefined) continue;
    const id = String(a.contour_id);
    const s = sums.get(id) ?? { req: 0, del: 0 };
    const req = a.requested_volume_m3 === null || a.requested_volume_m3 === undefined ? null : Number(a.requested_volume_m3);
    const del = a.volume_m3 === null || a.volume_m3 === undefined ? null : Number(a.volume_m3);
    s.req = s.req === null || req === null ? null : s.req + req;
    s.del = s.del === null || del === null ? null : s.del + del;
    sums.set(id, s);
  }
  const out: Record<string, WaterState> = {};
  for (const [id, s] of sums) {
    out[id] = s.req === null || s.del === null ? 'unmeasured' : s.del <= 0 ? 'none' : s.del < 0.95 * s.req ? 'partial' : 'delivered';
  }
  return out;
}

/* ------------------------------------------------------------ Расхождения */

type QueueRow = Schemas['VerificationQueueRowOut'];

export interface ConflictRow {
  farmObligationId: number;
  work: string;
  windowTo: string | null;
  /** Контуров с расхождением или с просьбой уточнить. */
  contours: number;
  outcome: 'conflict' | 'needs_clarification';
  checkedOn: string | null;
  checkedBy: string | null;
  /** Разбор сервера: один контур, отчёт, примечание и исправление статуса. */
  contourId?: string;
  contourNo?: string;
  reportedOn?: string | null;
  note?: string | null;
  correctedOn?: string | null;
  correctedBy?: string | null;
}

const ROLE_LABEL: Record<string, PlainMessage> = {
  verifier: msg.roleVerifier,
  hokimiyat_manager: msg.roleManager,
  farm: msg.roleFarm,
};
/** Роль отметившего на языке интерфейса; незнакомая — как есть. */
export const roleLabel = (role: string | null | undefined) => (role ? (ROLE_LABEL[role] ? tr(ROLE_LABEL[role]) : role) : null);

const OUTCOME_LABEL: Record<ConflictRow['outcome'], PlainMessage> = {
  conflict: msg.outcomeConflict,
  needs_clarification: msg.outcomeClarify,
};
export const outcomeLabel = (o: ConflictRow['outcome']) => tr(OUTCOME_LABEL[o]);

/** Спорные обязательства хозяйства из очереди проверки. */
export function conflictRows(items: readonly QueueRow[]): ConflictRow[] {
  const rows: ConflictRow[] = [];
  for (const it of items) {
    const conflict = it.by_state.find(b => b.key === 'conflict')?.count ?? 0;
    const clarify = it.by_state.find(b => b.key === 'needs_clarification')?.count ?? 0;
    if (conflict + clarify === 0) continue;
    rows.push({
      farmObligationId: it.farm_obligation_id,
      work: it.work_name,
      windowTo: it.window_to ?? null,
      contours: conflict + clarify,
      outcome: conflict > 0 ? 'conflict' : 'needs_clarification',
      checkedOn: it.last_verification_on ?? null,
      checkedBy: roleLabel(it.last_verification_role) ?? it.last_verification_by ?? null,
    });
  }
  return rows.sort((a, b) => Number(b.outcome === 'conflict') - Number(a.outcome === 'conflict') || b.contours - a.contours);
}

/** Контуры хозяйства с расхождениями — из разреза картины по контурам. */
export function conflictContours(byContour: readonly BreakdownRow[]): string[] {
  return byContour.filter(r => (val(r.totals.conflicts) ?? 0) > 0).map(r => String(r.id));
}

/**
 * Слой карты разбора «Расхождения» (agroai-6al.15): спорные контуры
 * подкрашены цветом проблемы, остальная земля хозяйства — без значения.
 * Прежде кадр красил культуру, и спорный контур без культуры сезона был
 * серым, как «размещение неизвестно» — выделения не было вовсе.
 *
 * Тон — `warning`: расхождение требует внимания, но это спор о записи, а не
 * критичное состояние объекта (ADR-0009: красный — только объект). Смысл
 * держит не один цвет: подпись легенды, обводка и строка разбора рядом.
 */
export function conflictLayer(ids: readonly string[]): ConflictLayer {
  const byId: Record<string, string> = {};
  for (const id of ids) byId[id] = CONFLICT_KEY;
  return {
    title: tr(msg.layerTitle),
    byId,
    legend: [{ key: CONFLICT_KEY, label: tr(msg.layerLegend), tone: 'warning' }],
    noneLabel: tr(msg.layerNone),
  };
}

export const CONFLICT_KEY = 'conflict';

/** Форма произвольного слоя карты (`map/layers.ts: ValuesLayer`) без импорта карты в модель. */
export interface ConflictLayer {
  title: string;
  byId: Record<string, string>;
  legend: { key: string; label: string; tone: 'warning' }[];
  noneLabel: string;
}

/* ------------------------------------------------------------ Карантин */

type QRow = Schemas['QStatusRow'];

export interface QuarantineRow {
  contourId: string;
  contourNo: string;
  status: 'red' | 'yellow' | 'green';
  since: string;
  pest: string | null;
  deadline: string | null;
  /** Дней после срока предписания; `null` — предписание в срок или его нет. */
  overdueDays: number | null;
}

/** Красные/жёлтые контуры и предписания после срока на земле хозяйства. */
export function quarantineRows(items: readonly QRow[], farmContours: ReadonlySet<string>, asOf: string): QuarantineRow[] {
  return items
    .filter(it => farmContours.has(String(it.contour_id)))
    .filter(it => it.status === 'red' || it.status === 'yellow' || it.requirement_overdue === true)
    .map(it => ({
      contourId: String(it.contour_id),
      contourNo: it.contour_no,
      status: it.status as QuarantineRow['status'],
      since: it.effective_on,
      pest: it.problem_name ?? null,
      deadline: it.requirement_deadline ?? null,
      overdueDays: it.requirement_overdue && it.requirement_deadline ? Math.max(0, daysFrom(it.requirement_deadline, asOf)) : null,
    }))
    .sort((a, b) => Number(b.status === 'red') - Number(a.status === 'red') || (b.overdueDays ?? -1) - (a.overdueDays ?? -1));
}

/* ------------------------------------------------------------ KPI → вкладка */

/** Клик по KPI Обзора: какая вкладка «Где нужно разобраться» включается. */
export const KPI_TAB = {
  overdue: 'deadlines',
  water: 'water',
  quarantine: 'quarantine',
} as const satisfies Record<string, ProblemTab>;

/** Вкладка из адреса (`?kind=`), неизвестное — «Все». */
export function tabFromParam(v: string | null): ProblemTab {
  return TABS.some(t => t.id === v) ? (v as ProblemTab) : 'all';
}

/* ------------------------------------------------------------ разбор сервера (6al.2) */

/**
 * `GET /season/farm-breakdown` — разбор хозяйства по типу сигнала. Раздел
 * `null` — сигнала этого типа на дату нет (наблюдение о данных, не оценка).
 * Ниже — перевод разделов в строки шаблонов `ReviewByKind`; картина сезона
 * хозяйства нужна только для ссылок на работу (кампания) и площади.
 */
export type FarmBreakdown = Schemas['FarmBreakdownOut'];
type BreakdownWork = Schemas['BreakdownWorkOut'];

function baseWork(w: BreakdownWork | Schemas['BreakdownWaterWorkOut'], works: readonly CampaignWork[], asOf: string): OverdueWork {
  const found = works.find(x => x.work_def_id === w.work_def_id);
  if (found) return toOverdueWork(found, asOf);
  return {
    campaignId: 0,
    workDefId: w.work_def_id,
    name: workName(w.name),
    category: '',
    obligations: w.obligations,
    overdue: 0,
    noReport: 0,
    areaHa: null,
    windowFrom: 'window_from' in w ? w.window_from ?? null : null,
    windowTo: w.window_to ?? null,
    closedDaysAgo: w.window_to ? daysFrom(w.window_to, asOf) : null,
    silentContours: [],
  };
}

/** «Больше всего после срока»: работы раздела `deadlines` (сервер уже отсортировал). */
export function breakdownOverdue(d: FarmBreakdown['deadlines'], works: readonly CampaignWork[], asOf: string, n = 3): OverdueWork[] {
  return (d?.overdue_works ?? []).slice(0, n).map(w => ({
    ...baseWork(w, works, asOf),
    name: workName(w.name),
    overdue: w.obligations,
    // Земля именно этих работ после срока (сервер), а не вся площадь работы.
    areaHa: w.area_ha ?? null,
    closedDaysAgo: w.days_overdue ?? (w.window_to ? daysFrom(w.window_to, asOf) : null),
    silentContours: w.contours.map(c => String(c.contour_id)),
  }));
}

/** «Окна закрываются в ближайшие дни»: работы раздела `deadlines.due_soon_works`. */
export function breakdownSoon(d: FarmBreakdown['deadlines'], works: readonly CampaignWork[], asOf: string): OverdueWork[] {
  return (d?.due_soon_works ?? []).map(w => ({
    ...baseWork(w, works, asOf),
    name: workName(w.name),
    noReport: w.obligations,
    windowTo: w.window_to ?? null,
    closedDaysAgo: w.days_left === null || w.days_left === undefined ? null : -w.days_left,
    silentContours: w.contours.map(c => String(c.contour_id)),
  }));
}

/**
 * Поливы хозяйства из раздела `water`: запрошено → подано по поливу (через
 * подачи, закрывшие его обязательства, REL-031). `null` объёма — объёма нет в
 * записях, а не ноль. Контуры без отчёта — из картины (это про отчёт), контуры
 * с неподтверждённой подачей — из разбора.
 */
export function breakdownIrrigation(water: FarmBreakdown['water'], works: readonly CampaignWork[], asOf: string): IrrigationRow[] {
  return (water?.works ?? [])
    .map(w => {
      const base = baseWork(w, works, asOf);
      const windowFrom = w.window_from ?? base.windowFrom;
      const requested = w.requested_m3;
      const delivered = w.delivered_m3;
      return {
        campaignId: base.campaignId,
        cycle: base.cycle ?? null,
        workDefId: w.work_def_id,
        name: workName(w.name),
        windowFrom,
        windowTo: w.window_to ?? base.windowTo,
        requested,
        delivered,
        gap: requested === null || delivered === null ? null : Math.max(0, requested - delivered),
        silentContours: windowFrom !== null && windowFrom <= asOf ? base.silentContours : [],
        ahead: windowFrom !== null && windowFrom > asOf,
        noRequests: requested === null && delivered === null,
        gapContours: w.gap_contours.map(c => String(c.contour_id)),
        gapContoursTotal: w.gap_contours_total,
        gapContourNos: w.gap_contours.map(c => c.contour_no),
        fromEvents: water?.source === 'events',
        obligations: w.obligations,
        closed: w.closed,
        wateredContours: w.watered_contours_total ?? 0,
        events: w.events_total ?? 0,
        wateredHa: w.watered_ha ?? null,
      };
    })
    .sort((a, b) => (a.windowFrom ?? '').localeCompare(b.windowFrom ?? ''));
}

/** Расхождения из раздела `conflicts`: открытые и «статус исправлен» (кем, когда). */
export function breakdownConflicts(c: FarmBreakdown['conflicts']): ConflictRow[] {
  const row = (r: Schemas['BreakdownConflictOut'], corrected: boolean): ConflictRow => ({
    farmObligationId: r.obligation_id,
    work: workName(r.work_name),
    windowTo: null,
    contours: 1,
    outcome: 'conflict',
    checkedOn: r.checked_on ?? null,
    checkedBy: roleLabel(r.checked_by) ?? null,
    contourId: String(r.contour.contour_id),
    contourNo: r.contour.contour_no,
    reportedOn: r.reported_on ?? null,
    note: r.note || null,
    correctedOn: corrected ? r.corrected_on ?? null : null,
    correctedBy: corrected ? r.corrected_by ?? null : null,
  });
  return [...(c?.open ?? []).map(r => row(r, false)), ...(c?.corrected ?? []).map(r => row(r, true))];
}

const Q_STATUS: Record<string, QuarantineRow['status']> = { red: 'red', yellow: 'yellow', watch: 'yellow', green: 'green' };

/** Карантин из раздела `quarantine`: контуры красн./жёлт. и предписания после срока. */
export function breakdownQuarantine(q: FarmBreakdown['quarantine']): QuarantineRow[] {
  return (q?.contours ?? [])
    .map(r => ({
      contourId: String(r.contour_id),
      contourNo: r.contour_no,
      status: Q_STATUS[r.status] ?? 'green',
      since: r.status_on ?? '',
      pest: r.problem_name ?? null,
      deadline: r.requirement_deadline ?? null,
      overdueDays: r.days_overdue ?? null,
    }))
    .sort((a, b) => Number(b.status === 'red') - Number(a.status === 'red') || (b.overdueDays ?? -1) - (a.overdueDays ?? -1));
}

/** Типы проблем хозяйства, которые назвал разбор сервера (порядок карточек). */
export function breakdownKinds(b: FarmBreakdown | null | undefined): ProblemKind[] {
  if (!b) return [];
  const out: ProblemKind[] = [];
  if (b.deadlines && (b.deadlines.overdue_total > 0 || b.deadlines.due_soon_total > 0)) out.push('deadlines');
  if (b.water && b.water.works.some(w => w.gap_contours_total > 0)) out.push('water');
  if (b.conflicts && b.conflicts.open_total > 0) out.push('conflicts');
  // `contours_total` — вся земля хозяйства (знаменатель), сигнал — строки контуров.
  if (b.quarantine && b.quarantine.contours.length > 0) out.push('quarantine');
  return out;
}


/* ------------------------------------------------------------ направления хозяйства */

/**
 * Направление разбора хозяйства — вкладка центральной карточки «Где нужно
 * разобраться» (agroai-0e8). «Совпадение» отдельным направлением не живёт:
 * с водой по событиям файла это тот же полив без подачи — вкладка «Вода».
 */
export type Direction = Exclude<ProblemKind, 'overlap'>;

/** Порядок тяжести (agroai-0e8): сроки, вода, карантин — раньше расхождений отчётов. */
export const DIRECTION_ORDER: Direction[] = ['deadlines', 'water', 'quarantine', 'conflicts'];

/** Тип строки очереди → направление («Совпадение» — вода). */
export const directionOf = (kind: ProblemKind): Direction => (kind === 'overlap' ? 'water' : kind);

/**
 * Направления хозяйства, где есть проблема, — по тем же числам, что значки
 * строки (`signalCounts`): вкладка и значок не расходятся. Выбранная
 * категория очереди добавляется всегда: сервер уже отнёс хозяйство к ней.
 */
export function farmDirections(s: Situation, tab: ProblemTab = 'all'): Direction[] {
  const c = signalCounts(s);
  const has: Record<Direction, boolean> = {
    deadlines: (c.deadlines ?? 0) > 0,
    water: (c.waterObligations ?? 0) > 0,
    conflicts: (c.conflicts ?? 0) > 0,
    quarantine: (c.quarantine ?? 0) > 0,
  };
  if (tab !== 'all') has[tab] = true;
  return DIRECTION_ORDER.filter(d => has[d]);
}

/**
 * Направление по умолчанию: выбранная категория, иначе главный сигнал строки,
 * иначе первое по тяжести. Расхождение отчётов главным не открывается, если у
 * хозяйства есть сроки, вода или карантин (agroai-0e8): вид ситуации сервера
 * ставит расхождения первыми ради ключа ситуации, а не по тяжести.
 */
export function defaultDirection(s: Situation, tab: ProblemTab, dirs: readonly Direction[]): Direction {
  if (tab !== 'all' && dirs.includes(tab)) return tab;
  const main = directionOf(kindOfSignal(s.kind));
  if (main !== 'conflicts' && dirs.includes(main)) return main;
  return dirs[0] ?? main;
}

/** Короткое число на вкладке направления — то же, что на значке строки. */
export function directionCount(d: Direction, s: Situation): string | null {
  const c = signalCounts(s);
  switch (d) {
    case 'deadlines':
      if (typeof c.deadlinesHa === 'number' && c.deadlinesHa > 0) return tr(msg.dirDeadlinesHa, { ha: num(Math.max(1, Math.round(c.deadlinesHa))) });
      return c.deadlines ? tr(msg.dirDeadlinesWorks, { count: num(c.deadlines) }) : null;
    case 'water':
      return c.waterObligations ? tr(msg.dirWaterCount, { count: num(c.waterObligations) }) : null;
    case 'conflicts':
      return c.conflicts ? tr(msg.dirConflictsCount, { count: num(c.conflicts) }) : null;
    case 'quarantine':
      return c.quarantine ? tr(msg.dirQuarantineCount, { contours: countOf(c.quarantine, WORDS.contour) }) : null;
  }
}

/* ------------------------------------------------------------ слой карты «Сроки»: только проблемные контуры */

export type DeadlineMapState = 'overdue' | 'closing';

export interface DeadlineLayer {
  title: string;
  byId: Record<string, DeadlineMapState>;
  legend: { key: DeadlineMapState; label: string; tone: 'warning' | 'progress' }[];
  noneLabel: string;
}

/**
 * Слой «Сроки» выбранной работы (agroai-0e8): подсвечены только контуры с
 * проблемой — после срока без отчёта и (если окно идёт) без отчёта в
 * закрывающемся окне. Остальная земля хозяйства — фон без значения.
 */
export function deadlineLayer(status: Readonly<Record<string, string>>, closing: boolean): DeadlineLayer {
  const byId: Record<string, DeadlineMapState> = {};
  for (const [id, v] of Object.entries(status)) {
    if (v === 'silent') byId[id] = 'overdue';
    else if (closing && v === 'in_window') byId[id] = 'closing';
  }
  return {
    title: tr(msg.deadlineLayerTitle),
    byId,
    legend: [
      { key: 'overdue', label: tr(msg.deadlineOverdue), tone: 'warning' },
      ...(closing ? [{ key: 'closing' as const, label: tr(msg.deadlineClosing), tone: 'progress' as const }] : []),
    ],
    noneLabel: tr(msg.problemLayerNone),
  };
}

/* ------------------------------------------------------------ слой карты «Вода» по событиям */

export type WaterEventState = 'no_delivery';

/** Форма слоя значений карты (`map/layers.ts: ValuesLayer`) без импорта карты в модель. */
export interface WaterEventLayer {
  title: string;
  byId: Record<string, WaterEventState>;
  legend: { key: WaterEventState; label: string; tone: 'warning' }[];
  noneLabel: string;
}

/**
 * Слой карты «Вода» по событиям файла ирригации (agroai-0e8): статус полива
 * по контурам с сервера (`season/contour-status`). Отчёт о поливе выводится
 * ТОЛЬКО из события файла (автозакрытие, №725), поэтому «отчёт есть» здесь —
 * «полито по файлу», «после срока без отчёта» — «без подачи».
 */
export function waterEventLayer(status: Readonly<Record<string, string>>): WaterEventLayer {
  const byId: Record<string, WaterEventState> = {};
  // «Где нужно разобраться» — подсвечены только контуры без подачи; политые
  // и прочие — фон (правило владельца: карта — только проблемные контуры).
  for (const [id, v] of Object.entries(status)) if (v === 'silent') byId[id] = 'no_delivery';
  return {
    title: tr(msg.waterLayerTitle),
    byId,
    legend: [{ key: 'no_delivery', label: tr(msg.waterNoDelivery), tone: 'warning' }],
    noneLabel: tr(msg.waterLayerNone),
  };
}
